import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const THEMES = [
  { value: 'system', label: 'Automatic', hint: 'Follows your device' },
  { value: 'light', label: 'Light', hint: 'Warm paper' },
  { value: 'dark', label: 'Dark', hint: 'Easy at night' },
  { value: 'black', label: 'Black', hint: 'True black for OLED' },
];

export const ACCENTS = [
  { value: 'coral', label: 'Coral', color: '#dd5236' },
  { value: 'ocean', label: 'Ocean', color: '#2a68b0' },
  { value: 'forest', label: 'Forest', color: '#2b7a53' },
  { value: 'berry', label: 'Berry', color: '#b0346a' },
  { value: 'amber', label: 'Amber', color: '#b86f0a' },
  { value: 'graphite', label: 'Graphite', color: '#33373e' },
];

export const TEXT_SIZES = [
  { value: 's', label: 'Small' },
  { value: 'm', label: 'Default' },
  { value: 'l', label: 'Large' },
  { value: 'xl', label: 'Extra large' },
];

/**
 * Preferences. When this device joins shared settings, every field except
 * `onboarded`, `tipsSeen`, and `accountPromptDismissed` syncs. Those stay on this device.
 * The join choice itself lives in IndexedDB meta (`settingsJoin`), not here.
 */
export const useSettings = create(
  persist(
    (set) => ({
      theme: 'system',
      accent: 'coral',
      textSize: 'm',
      cardFont: 'serif',
      cardAlign: 'center',
      motion: 'system',
      ratingButtons: 4,
      swipe: { left: 1, right: 3, up: 4, down: 2 },
      swipeEnabled: true,
      showIntervals: true,
      haptics: true,
      autoCloudBackup: true,
      // Layout
      sidebar: 'full', // 'full' | 'icons' | 'hidden' (tablet and desktop)
      sidebarDecks: true,
      width: 'comfortable', // 'comfortable' | 'wide'
      density: 'comfortable', // 'comfortable' | 'compact'
      tabLabels: true,
      homeToday: true,
      // Behavior
      afterAdd: 'stay', // 'stay' | 'back'
      addPreview: true,
      studyProgress: true,
      studyChip: true,
      onboarded: false,
      tipsSeen: {},
      // Home-screen account prompt. Dismissed stays dismissed on this device.
      accountPromptDismissed: false,
      set: (patch) => set(patch),
      seeTip: (id) => set((s) => ({ tipsSeen: { ...s.tipsSeen, [id]: true } })),
    }),
    {
      name: 'recall.settings',
      version: 2,
      migrate: (state) => ({ ...state }),
    },
  ),
);

const THEME_COLORS = { light: '#f3f1ec', dark: '#121316', black: '#000000' };

function resolvedTheme(theme) {
  if (theme !== 'system') return theme;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyAppearance({
  theme,
  accent,
  textSize,
  cardFont,
  cardAlign,
  motion,
  density,
  width,
}) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.dataset.accent = accent;
  root.dataset.textSize = textSize;
  root.dataset.cardFont = cardFont;
  root.dataset.cardAlign = cardAlign;
  root.dataset.density = density ?? 'comfortable';
  root.dataset.width = width ?? 'comfortable';
  if (motion === 'reduced') root.dataset.motion = 'reduced';
  else delete root.dataset.motion;
  const color = THEME_COLORS[resolvedTheme(theme)];
  document
    .querySelectorAll('meta[name="theme-color"]')
    .forEach((m) => m.setAttribute('content', color));
}

/** Short vibration for touch feedback where supported (Android); a no-op elsewhere. */
export function haptic(pattern = 8) {
  if (!useSettings.getState().haptics) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Some browsers throw when called without a user gesture.
  }
}

export const canVibrate =
  typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
